/**
 * Shop the Look Section JavaScript
 * 
 * This module handles the interactive functionality for the shop-the-look section,
 * including Swiper carousel initialization, slide transitions, and product pin interactions.
 * 
 * @author UrbanRoad Theme
 * @version 1.0.0
 */

// Media query to detect large screen sizes (minimum width of 991px)
const largeScreenBreak = window.matchMedia("(min-width: 991px)");

/**
 * Updates mobile title and button elements based on the currently active slide
 * 
 * @param {string} sectionId - The unique identifier for the shop-the-look section
 * @param {number} activeIndex - The index of the currently active slide
 */
function updateSlideElements(sectionId, activeIndex) {
  // Find the image info element for the active slide
  const currentSlideData = document.querySelector(
    `#shop-the-look-wrapper-template--${sectionId} .shop-the-look__image-info--${activeIndex}`
  );
  
  // Get mobile title and button elements
  const titleEl = document.querySelector(
    `#shop-the-look-wrapper-template--${sectionId} .shop-the-look__image-title_mobile`
  );
  const buttonEl = document.querySelector(
    `#shop-the-look-wrapper-template--${sectionId} .shop-the-look__categories-button`
  );

  // Update elements with content from the active slide if data exists
  if (currentSlideData) {
    const titleText = currentSlideData.querySelector("p")?.textContent;
    const linkElement = currentSlideData.querySelector("a");
    
    if (titleText && titleEl) {
      titleEl.textContent = titleText;
    }
    
    if (linkElement && buttonEl) {
      buttonEl.href = linkElement.href;
      buttonEl.textContent = linkElement.textContent;
    }
  }
}

/**
 * Initialize Swiper carousel instances for all shop-the-look sections
 * 
 * Creates fade-effect carousels with autoplay functionality and progress indicators.
 * Each carousel is configured with responsive behavior and interactive elements.
 */
document
  .querySelectorAll(".shop-the-look-swiper")
  .forEach((swiperContainer) => {
    // Extract section ID from the swiper container ID
    const sectionId = swiperContainer.id.split("-").pop();
    
    // Get progress bar and pagination counter elements
    const autoplayProgress = document.querySelector(
      `#autoplayProgress-template--${sectionId}`
    );
    const paginationCounter = document.querySelector(
      `#swiper-pagination-counter-template--${sectionId} span`
    );

    // Initialize Swiper with fade effect configuration
    new Swiper(`#${swiperContainer.id}`, {
      effect: "fade", // Smooth fade transition between slides
      fadeEffect: { 
        crossFade: true // Cross-fade effect for smoother transitions
      },
      loop: false, // Disable infinite loop
      watchSlidesProgress: true, // Enable slide progress tracking
      spaceBetween: 30, // Space between slides (not used in fade effect)
      centeredSlides: true, // Center slides in viewport
      
      // Autoplay configuration
      autoplay: {
        delay: 6000, // 6 seconds between automatic slide changes
        disableOnInteraction: false, // Continue autoplay after user interaction
        pauseOnMouseEnter: true, // Pause autoplay when hovering over carousel
      },
      
      // Pagination configuration
      pagination: {
        el: `#shop-the-look-swiper-template--${sectionId} .swiper-pagination`,
        clickable: true, // Allow users to click pagination dots to navigate
      },
      
      // Event handlers
      on: {
        /**
         * Initialize event handler
         * Updates mobile elements when Swiper is first initialized
         */
        init(swiper) {
          updateSlideElements(sectionId, swiper.activeIndex);
        },
        
        /**
         * Slide change event handler
         * Updates mobile elements whenever the active slide changes
         */
        slideChange(swiper) {
          updateSlideElements(sectionId, swiper.activeIndex);
        },
        
        /**
         * Autoplay progress event handler
         * Updates progress bar and pagination counter during autoplay
         */
        autoplayTimeLeft(swiper, time, progress) {
          // Update progress bar (inverted value for visual effect)
          if (autoplayProgress) {
            autoplayProgress.value = 1 - progress;
          }
          
          // Update pagination counter with current slide number
          if (paginationCounter) {
            paginationCounter.textContent = swiper.realIndex + 1;
          }
        },
      },
    });
  });

/**
 * Product Pin Interaction Handler
 * 
 * Manages the interactive behavior of product pins on shop-the-look images.
 * Ensures only one pin can be active at a time and handles pin state changes.
 */

// Get all checkbox elements for product pins
const checkboxPins = document.querySelectorAll(
  ".shop-the-look__product-checkbox"
);

/**
 * Add event listeners to all product pin checkboxes
 * 
 * Handles pin activation/deactivation with single-pin selection behavior.
 * Only one pin can be active at any given time.
 */
checkboxPins.forEach((checkboxPin) => {
  checkboxPin.addEventListener("change", (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    // Find the currently active pin (if any)
    const activePin = document.querySelector(
      ".shop-the-look__product-checkbox.active"
    );

    // If there's an active pin different from the current one, deactivate it
    if (activePin && activePin !== checkboxPin) {
      activePin.classList.remove("active");
    }

    // Toggle the active state of the clicked pin
    checkboxPin.classList.toggle("active");
  });
});

/**
 * Category Item Navigation Handler
 * 
 * Manages the interaction between category items and the Swiper carousel.
 * Provides different interaction modes for desktop (hover) and mobile (click) devices.
 */

// Initialize category item interactions for all shop-the-look sections
document
  .querySelectorAll(".shop-the-look__categories-item-popup")
  .forEach((categoryItem) => {
    // Extract section ID from the category item's data attribute
    const sectionId = categoryItem.dataset.sectionId;
    const largeScreenBreak = window.matchMedia("(min-width: 991px)");

    /**
     * Creates event handlers for category item interactions
     * 
     * @param {string} event - The event type to listen for ('mouseover' or 'click')
     * @param {boolean} scrollIntoView - Whether to scroll the section into view on mobile
     */
    const handleShopLookEvent = (event, scrollIntoView = false) => {
      categoryItem.addEventListener(event, function (e) {
        // Get the link element within the category item
        const linkElement = categoryItem.querySelector(
          ".shop-the-look__categories-item_link"
        );

        // Skip navigation if the user clicked on the link element itself
        if (
          linkElement &&
          (e.target === linkElement || linkElement.contains(e.target))
        ) {
          return; // Allow default link behavior
        }

        // Get slide index and parse it as an integer
        const slideIndex = categoryItem.dataset.slideIndex;
        const index = parseInt(slideIndex);

        // Get the Swiper instance for this section
        const swiperElement = document.querySelector(
          `#shop-the-look-swiper-${sectionId}`
        );
        
        if (!swiperElement || !swiperElement.swiper) {
          console.error("Swiper instance not found for section:", sectionId);
          return;
        }
        
        const swiperInstance = swiperElement.swiper;

        // Validate slide index and navigate to the specified slide
        if (!isNaN(index) && index >= 0) {
          // Scroll section into view on mobile devices if requested
          if (scrollIntoView && !largeScreenBreak.matches) {
            const parentElement = categoryItem.closest(".shopify-section");
            if (parentElement) {
              parentElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
              });
            }
          }
          
          // Navigate to the specified slide
          swiperInstance.slideTo(index);
        } else {
          console.error("Invalid slide index:", slideIndex);
        }

        e.preventDefault(); // Prevent default event behavior
      });
    };

    // Set up event listeners based on screen size
    if (largeScreenBreak.matches) {
      // Desktop: Use hover events for immediate slide switching
      handleShopLookEvent("mouseover");
    } else {
      // Mobile: Use click events with scroll-to-view functionality
      handleShopLookEvent("click", true);
    }
  });